import api from './api';
import type { WineListItem, PaginatedResponse } from '../types/venue';

const base = (venueId: string, wineListId: string) =>
  `/admin/venue/${venueId}/wine-list/${wineListId}/item`;

const wineListItemService = {
  /** Get all items on a wine list. */
  getItems: async (
    venueId: string,
    wineListId: string,
    page = 1,
    pageSize = 50,
    searchQuery?: string,
    visibility?: string
  ): Promise<PaginatedResponse<WineListItem>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (searchQuery?.trim()) params.q = searchQuery.trim();
    if (visibility) params.visibility = visibility;
    const response = await api.get(base(venueId, wineListId), { params });
    return response.data;
  },

  /** Get a single wine list item. */
  getItem: async (
    venueId: string,
    wineListId: string,
    itemId: string
  ): Promise<WineListItem> => {
    const response = await api.get(`${base(venueId, wineListId)}/${itemId}`);
    return response.data;
  },

  /** Add a wine to a wine list. */
  createItem: async (
    venueId: string,
    wineListId: string,
    data: Omit<WineListItem, '_id' | 'wineListId' | 'venueId' | 'createdAt' | 'updatedAt'>
  ): Promise<WineListItem> => {
    const response = await api.post(base(venueId, wineListId), data);
    return response.data;
  },

  /** Update a wine list item (price, visibility, bin, etc.). inventoryItemId may be provided once to link a previously unlinked item. */
  updateItem: async (
    venueId: string,
    wineListId: string,
    itemId: string,
    data: Partial<Pick<WineListItem, 'listPrice' | 'currency' | 'binNumber' | 'visibility' | 'notes' | 'position' | 'active' | 'bottleSize'>> & { inventoryItemId?: string }
  ): Promise<WineListItem> => {
    const response = await api.put(`${base(venueId, wineListId)}/${itemId}`, data);
    return response.data;
  },

  /** Remove a wine from a wine list. */
  deleteItem: async (
    venueId: string,
    wineListId: string,
    itemId: string
  ): Promise<void> => {
    await api.delete(`${base(venueId, wineListId)}/${itemId}`);
  },
};

export default wineListItemService;
